import { useCallback, useState } from 'react';
import type { Workspace } from '@workspace/domain/workspace/Workspace';
import type { InternalPublicSetting } from './EditModal';
import { ShareSettingEditModal } from './EditModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

type Props = {
  workspace: Workspace;
  internalPublicSetting: InternalPublicSetting;
  isEditable: boolean;
  groupEnabledIndividualViewModelPermissions: boolean;
};

export const ShareSetting: React.FC<Props> = ({
  workspace,
  internalPublicSetting,
  isEditable,
  groupEnabledIndividualViewModelPermissions,
}: Props) => {
  const { t } = useTranslation();
  const [settingEditModalOpen, setSettingEditModalOpen] = useState<boolean>(false);
  const handleEditStart = useCallback(() => setSettingEditModalOpen(true), []);
  const handleEditEnd = useCallback(() => setSettingEditModalOpen(false), []);

  return (
    <div className="flex w-full flex-col">
      <div className="mb-1 flex flex-row items-center justify-between border-b border-gray-500">
        <div className="text-2xl font-bold">{t('workspace.shareSetting.title')}</div>
        {isEditable && (
          <div className="mx-2 block size-4 cursor-pointer" onClick={handleEditStart}>
            <FontAwesomeIcon icon={faCog} className="text-lg" />
          </div>
        )}
      </div>

      <div>
        {/* ワークスペースの状態 */}
        {workspace.setting.isPublicSpace ? (
          <>
            <div className="pt-1 font-bold">{t('workspace.shareSetting.publicStatus')}</div>
            <div className="pt-1 text-sm">{t('workspace.shareSetting.publicDescription')}</div>
          </>
        ) : internalPublicSetting ? (
          <>
            <div className="pt-1 font-bold">{t('workspace.shareSetting.internalPublicStatus')}</div>
            <div className="pt-1 text-sm">{t('workspace.shareSetting.internalPublicDescription')}</div>
          </>
        ) : (
          <>
            <div className="pt-1 font-bold">{t('workspace.shareSetting.privateStatus')}</div>
            <div className="pt-1 text-sm">{t('workspace.shareSetting.privateDescription')}</div>
          </>
        )}
      </div>

      <div className="pt-2">
        {workspace.setting.isViewModelURLShareable ? (
          <>
            {/* 外部公開ワークスペースはOFFで、ビューモデルごとの外部公開が有効の場合 */}
            <div className="pt-1 font-bold">{t('workspace.shareSetting.urlShareableStatus')}</div>
            <div className="pt-1 text-sm">{t('workspace.shareSetting.urlShareableDescription')}</div>
          </>
        ) : (
          <>
            <div className="pt-1 font-bold">{t('workspace.shareSetting.urlNotShareableStatus')}</div>
            <div className="pt-1 text-sm">{t('workspace.shareSetting.urlNotShareableDescription')}</div>
          </>
        )}
      </div>

      <div className="pt-2">
        {groupEnabledIndividualViewModelPermissions && workspace.setting.enableIndividualViewModelPermissions ? (
          <>
            <div className="pt-1 font-bold">{t('workspace.shareSetting.individualPermEnabled')}</div>
            <div className="pt-1 text-sm">{t('workspace.shareSetting.individualPermEnabledDesc')}</div>
          </>
        ) : (
          <>
            <div className="pt-1 font-bold">{t('workspace.shareSetting.individualPermDisabled')}</div>
            <div className="pt-1 text-sm">{t('workspace.shareSetting.individualPermDisabledDesc')}</div>
          </>
        )}
      </div>

      {settingEditModalOpen && (
        <ShareSettingEditModal
          onClose={handleEditEnd}
          workspace={workspace}
          internalPublicSetting={internalPublicSetting}
          groupEnabledIndividualViewModelPermissions={groupEnabledIndividualViewModelPermissions}
        />
      )}
    </div>
  );
};
